import React, { useMemo, useState } from "react";
import { getMediaUrl } from "../../utils/api";
import { normalizeAdminRichText } from "../../utils/richText";
import {
  EXTERNAL_VIDEO_IFRAME_PROPS,
  getVideoEmbedInfo,
} from "../../utils/videoEmbed";

function sortByTitle(items) {
  return [...items].sort((a, b) =>
    String(a?.titulo ?? "").localeCompare(String(b?.titulo ?? ""), "es", {
      sensitivity: "base",
    }),
  );
}

function getItemUrl(item) {
  const enlace = String(item?.enlace ?? "").trim();
  if (enlace) return enlace;
  const archivo = item?.archivo_url;
  if (archivo) return getMediaUrl(archivo);
  return "";
}

export default function ResourceListPanel({ data, itemsKey, emptyLabel }) {
  const items = useMemo(() => {
    const raw = data?.[itemsKey];
    return sortByTitle(Array.isArray(raw) ? raw : []);
  }, [data, itemsKey]);

  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter(
      (item) =>
        String(item?.titulo ?? "")
          .toLowerCase()
          .includes(q) ||
        String(item?.descripcion ?? "")
          .toLowerCase()
          .includes(q),
    );
  }, [items, query]);

  return (
    <div className="scm-resources">
      <div className="scm-search scm-search--full">
        <i className="bi bi-search scm-search__icon" aria-hidden />
        <input
          type="search"
          className="scm-search__input"
          placeholder="Buscar…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          aria-label="Buscar recursos"
        />
      </div>

      {filtered.length === 0 ? (
        <p className="scm-empty">{emptyLabel}</p>
      ) : (
        <div className="scm-resources__grid">
          {filtered.map((item) => {
            const url = getItemUrl(item);
            const desc = String(item?.descripcion ?? "").trim();
            const video = url ? getVideoEmbedInfo(url) : null;

            return (
              <article key={item.id} className="scm-resource-card">
                <div className="scm-resource-card__body">
                  <h3 className="scm-resource-card__title">{item.titulo}</h3>
                  {desc ? (
                    <div
                      className="scm-resource-card__desc rich-text"
                      dangerouslySetInnerHTML={{
                        __html: normalizeAdminRichText(desc),
                      }}
                    />
                  ) : null}
                </div>
                {video ? (
                  <div className="scm-resource-card__media">
                    {video.type === "iframe" ? (
                      <iframe
                        {...EXTERNAL_VIDEO_IFRAME_PROPS}
                        className="scm-resource-card__embed"
                        src={video.src}
                        title={item.titulo}
                      />
                    ) : (
                      <video
                        className="scm-resource-card__video"
                        controls
                        playsInline
                        preload="metadata"
                      >
                        <source src={video.src} />
                        <track kind="captions" />
                      </video>
                    )}
                    <a
                      className="scm-resource-card__link scm-resource-card__link--secondary"
                      href={video.originalUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <span>Abrir en nueva pestaña</span>
                      <i className="bi bi-box-arrow-up-right" aria-hidden />
                    </a>
                  </div>
                ) : url ? (
                  <a
                    className="scm-resource-card__link"
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <span>Consultar</span>
                    <i className="bi bi-box-arrow-up-right" aria-hidden />
                  </a>
                ) : null}
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
}
